<template>
  <ChartPanel
    :title="$t('realtime.opportunity.title')"
    icon="bi-cash-coin"
    :height="'auto'"
    class="panel-min-140"
    :loading="isLoadingTodayCount"
    :empty="!sortedOpportunityZones?.length"
    :accent="'#B2C149'"
  >
    <div class="table-wrapper">
      <table class="stora-table">
        <thead>
          <tr>
            <th>{{ $t('realtime.opportunity.zone') }}</th>
            <th>{{ $t('realtime.opportunity.visitors') }}</th>
            <th>{{ $t('realtime.opportunity.shareOfTotal') }}</th>
            <th>{{ $t('realtime.opportunity.engaged') }}</th>
            <th>{{ $t('realtime.opportunity.short') }}</th>
            <th>{{ $t('realtime.opportunity.clear') }}</th>
            <!-- Not the average over all visitors: this cell renders
                 avg_non_walkby_duration, the mean across everything above the
                 zone's walk-by cutoff — i.e. Short + Clear, the same set the
                 Engaged column is a percentage of. Plain "Avg. Duration" read as
                 all visitors and disagreed with the Zone Live Table's average
                 (which IS all visitors) for the same zone. -->
            <th>{{ $t('realtime.opportunity.avgEngagedDuration') }}</th>
            <th>{{ $t('realtime.opportunity.estConversion') }}</th>
            <th>{{ $t('realtime.opportunity.avgProductValue') }}</th>
            <th>{{ $t('realtime.opportunity.estSalesPotential') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="zoneData in sortedOpportunityZones" :key="zoneData.zoneName">
            <td :data-th="$t('realtime.opportunity.zone')" :style="getZoneStyle(zoneData.flowId, zoneData.zoneType).style" :title="zoneTypeTitle(zoneData.zoneType)">
              <b>{{ zoneData.zoneName }}</b>
            </td>

            <td :data-th="$t('realtime.opportunity.visitors')" :style="getZoneStyle(zoneData.flowId, zoneData.zoneType).style">
              {{ zoneData.todaysCount }}
            </td>

            <td :data-th="$t('realtime.opportunity.shareOfTotal')" :style="getZoneStyle(zoneData.flowId, zoneData.zoneType).style">
              <div class="visitor-bar-outer">
                <div
                  :class="['visitor-bar-gradient', {
                    'inactive': !getZoneStyle(zoneData.flowId, zoneData.zoneType).isStreamActive,
                    'zero-fill': zoneData.todaysCount === 0
                  }]"
                  :style="{ '--bar-fill': ((zoneData.todaysCount / totalVisitorCount) * 100) + '%' }"
                />
                <span v-if="getZoneStyle(zoneData.flowId, zoneData.zoneType).isStreamActive" class="visitor-bar-label">
                  {{ totalVisitorCount > 0 ? pct((zoneData.todaysCount / totalVisitorCount) * 100, 1) : '0%' }}
                </span>
              </div>
            </td>

            <td :data-th="$t('realtime.opportunity.engaged')" :style="getZoneStyle(zoneData.flowId, zoneData.zoneType).style">
              <span v-if="zoneData.count_strategy == 'insufficient_data'" class="muted">0%</span>
              <span v-else>
                {{ zoneData.todaysCount > 0 ? pct(((zoneData.shortLingerers + zoneData.clearLingerers) / zoneData.todaysCount) * 100, 1) : '0%' }}
              </span>
            </td>

            <td :data-th="$t('realtime.opportunity.short')" :style="getZoneStyle(zoneData.flowId, zoneData.zoneType).style">
              {{ zoneData.count_strategy == 'insufficient_data' ? 0 : zoneData.shortLingerers }}
            </td>

            <td :data-th="$t('realtime.opportunity.clear')" :style="getZoneStyle(zoneData.flowId, zoneData.zoneType).style">
              {{ zoneData.count_strategy == 'insufficient_data' ? 0 : zoneData.clearLingerers }}
            </td>

            <td :data-th="$t('realtime.opportunity.avgEngagedDuration')" :style="getZoneStyle(zoneData.flowId, zoneData.zoneType).style">
              <span v-if="zoneData.count_strategy == 'insufficient_data'" class="muted">{{ $t('realtime.opportunity.insufficientData') }}</span>
              <span v-else>{{ formattedDurationTime(zoneData.avgNonWalkbyDuration) }}</span>
            </td>

            <td :data-th="$t('realtime.opportunity.estConversion')" :style="getZoneStyle(zoneData.flowId, zoneData.zoneType).style">
              {{ pct(zoneData.conversionRate * 100, 0) }}
            </td>

            <td :data-th="$t('realtime.opportunity.avgProductValue')" :style="getZoneStyle(zoneData.flowId, zoneData.zoneType).style">
              {{ euro(zoneData.productValue) }}
            </td>

            <td :data-th="$t('realtime.opportunity.estSalesPotential')" :style="getZoneStyle(zoneData.flowId, zoneData.zoneType).style">
              <b>{{ euro((zoneData.shortLingerers + zoneData.clearLingerers) * zoneData.conversionRate * zoneData.productValue, 2) }}</b>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </ChartPanel>
</template>

<script>
import { onBeforeMount, onMounted, onBeforeUnmount, ref, computed, watch } from 'vue'

import Slider from '@vueform/slider'
import ChartPanel from '@/components/ui/ChartPanel.vue'

import { useCameraStreamStore } from '@/stores/CameraStreamStore'
import { useDetectionZoneStore } from '@/stores/DetectionZone'
import { useUserStore } from '@/stores/UserStore'
import { useCompanyStore } from '@/stores/CompanyStore'

import { detection_zone_colors_by_name_solid } from '@/constants.js'
import { useI18n } from 'vue-i18n'
import { zoneTypeLabel } from '@/i18n/zoneTypes'
import { formatEuro, formatPercent } from '@/i18n/format'
import { getCurrentCountersShared, abortGetCurrentCounters, getPastAverages } from "@/api/eventsAPI.js"

export default {
    name: 'CurrentLiveData',

    components: {
        Slider,
        ChartPanel
    },

    setup(props, { emit }) {
        const { t, locale } = useI18n()
        const CameraStreamStore = useCameraStreamStore()
        const userStore = useUserStore()
        const companyStore = useCompanyStore()
        const detectionZoneStore = useDetectionZoneStore()

        const totalTodaysCount = ref(0)
        const totalTodaysExit = ref(0)
        const pastAverages = ref({})
        const currentWeekday = ref(null)
        const isLoadingTodayCount = ref(true)
        const isLoadingTodayCountTable = ref(true)
        const processedZones = ref({})
        const flowZoneCounters = ref({})
        const streamToFlowMap = ref({})
        const streamStatuses = ref({})

        const zoneSettingsMap = ref({})
        const conversionRate = ref(0.1)
        const productValue = ref(0)

        const fetchInterval = ref(null)
        const isMounted = ref(true)
        let currentFetchController = null

        const formattedDurationTime = computed(() => {
            return (seconds) => {
                if (typeof seconds !== 'number' || isNaN(seconds)) {
                    return t('realtime.notAvailable'); // Handle invalid input
                }

                if (seconds < 60) {
                    return `${seconds.toFixed(0)} sec`
                } else if (seconds < 3600) {
                    const minutes = Math.floor(seconds / 60)
                    const remainingSeconds = seconds % 60
                    return `${minutes} min ${remainingSeconds.toFixed(0)} sec`
                } else {
                    const hours = Math.floor(seconds / 3600)
                    const remainingMinutes = Math.floor((seconds % 3600) / 60)
                    const remainingSeconds = seconds % 60
                    return `${hours} ${t('realtime.hourShort')} ${remainingMinutes} min ${remainingSeconds.toFixed(0)} sec`
                }
            }
        })

        const currentWeekdayString = computed(() => {
            if (currentWeekday.value >= 0 && currentWeekday.value <= 6) {
                const daysOfWeek = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"]
                return daysOfWeek[currentWeekday.value]
            } else {
                return 'Same Weekday'
            }
        })

        const stopInterval = () => {
            if (fetchInterval.value) {
                clearInterval(fetchInterval.value)
                fetchInterval.value = null
            }
        }

        const startInterval = () => {
            stopInterval() // Ensure any existing interval is cleared
            fetchInterval.value = setInterval(async () => {
                if (!isMounted.value) return // Exit if the component is not mounted
                await refreshData()
            }, 5000) // Refresh every 5 seconds
        }

        const refreshData = async () => {
            if (currentFetchController) {
                currentFetchController.abort() // Abort any ongoing fetch request
            }

            // if parent told us to stop, do nothing
            if (props.stopPollingCurrentCounters) {
                return
            }

            const controller = new AbortController()
            currentFetchController = controller
            try {
                const current_counters_data = await getCurrentCountersShared()
                if (isMounted.value) {
                    processedZones.value = processZonesData(current_counters_data)
                }
            } catch (error) {
                if (error.name === 'AbortError') {
                    console.log('Fetch aborted')
                } else {
                    console.error('Error fetching counters:', error)
                }
            }
        }

        const fetchStreamStatuses = async () => {
            const flowIds = Object.values(processedZones.value)
                .map(zone => zone.flowId) // Extract the flowId for each zone
                .filter((flowId, index, self) => self.indexOf(flowId) === index)
            
            for (const flowId of flowIds) {
                await CameraStreamStore.fetchInputCameraStreambyFlowId(flowId)
                const cameraStream = CameraStreamStore.inputcamerastream

                // Store the StreamStatus
                if (cameraStream && cameraStream.FlowId) {
                    streamStatuses.value[flowId] = cameraStream.StreamStatus
                }
            }
        }

        const getZoneStyle = (flowId, zoneType) => {
            const isStreamActive = streamStatuses.value[flowId] ?? false; // Default to false
            return {
                isStreamActive, // Include the stream activity status
                style: {
                    color: isStreamActive ? '#425461' : '#D3D3D3',
                    padding: '8px',
                    textAlign: 'left',
                    fontSize: 'small',
                },
            }
        }

        const getZoneStyleCurrentIn = (flowId, zoneType) => {
            const isStreamActive = streamStatuses.value[flowId] ?? false; // Default to false
            return {
                color: isStreamActive ? getColorForZone(zoneType) : '#D3D3D3',
                padding: '8px',
                textAlign: 'left',
                fontSize: 'small',
            }
        }

        const formatDifference = (difference) => {
            const roundedValue = Math.round(difference)
            return roundedValue > 0 ? `+${roundedValue}` : `${roundedValue}`
        }

        const getDifferenceColor = (difference) => {
            return difference > 0 ? '#36910d' : '#C21500'
        }

        const isCardEmpty = (...values) => {
            // Check if all values in the card are null, 0, empty string, or empty object
            return values.every(
                (val) =>
                    val === null ||
                    val === "null" ||
                    val === 0 ||
                    val === "0" ||
                    val === "" ||
                    (typeof val === "object" && Object.keys(val).length === 0)
            )
        }

        const initializeFlowData = () => {
            streamToFlowMap.value = CameraStreamStore.inputcamerastreams.reduce((acc, item) => {
                acc[item.CameraStreamId] = item.FlowId
                return acc
            }, {})
        }

        onBeforeMount(async () => {
            await companyStore.fetchCompanyDetails(userStore.currentCompany.CompanyId)
            await CameraStreamStore.fetchInputCameraStreams()
            await detectionZoneStore.fetchDetectionZones()

            zoneSettingsMap.value = detectionZoneStore.detectionzones.reduce((acc, zone) => {
                const settings = JSON.parse(zone.DetectionZoneSettings || '{}')
                acc[`${zone.FlowId}-${zone.DetectionZoneName}`] = {
                    conversionRate: settings.EngagersConversionRate ?? 0.1,
                    productValue: settings.AverageProductValue ?? 0,
                }
                return acc
            }, {})

            initializeFlowData()
        })

        onMounted(async () => {
            isMounted.value = true

            const current_counters_data = await getCurrentCountersShared()
            processedZones.value = processZonesData(current_counters_data)

            pastAverages.value = await getPastAverages()
            currentWeekday.value = pastAverages.value['current_weekday']

            isLoadingTodayCount.value = false

            // Initialize flowZoneCounters with default zero values
            for (const flowId in current_counters_data.current_counters_data) {
                const flowData = current_counters_data.current_counters_data[flowId]
                const counters = flowData.counters || {} // Get counters for this flowId

                // Ensure structure for the flowId
                if (!flowZoneCounters.value[flowId]) {
                    flowZoneCounters.value[flowId] = {}
                }

                // Initialize detection zones for the flowId
                for (const zoneName in counters) {
                    if (!flowZoneCounters.value[flowId][zoneName]) {
                        flowZoneCounters.value[flowId][zoneName] = 0 // Default value
                    }
                }
            }

            await fetchStreamStatuses(current_counters_data.current_counters_data)
            
            startInterval()
            
            isLoadingTodayCountTable.value = false
        })

        const processZonesData = (data) => {
            const zones = {}

            const flowData = data.current_counters_data

            if (!flowData) {
                console.warn("No 'current_counters_data' found")
                return zones
            }

            for (const flowId in flowData) {
                const flow = flowData[flowId]

                // A zone may appear in flow.counters (polygon zones), flow.store_counters
                // (Combined EE polygons + any zone with a tripwire), or both. Iterate the
                // union so tripwire-only zones aren't dropped from the opportunity view.
                const zoneNames = new Set([
                    ...Object.keys(flow.counters || {}),
                    ...Object.keys(flow.store_counters || {}),
                ])

                for (const zoneName of zoneNames) {
                    const counter = flow.counters?.[zoneName] || null
                    const storeCounter = flow.store_counters?.[zoneName] || null

                    const zoneKey = `${flowId}-${zoneName}`
                    const settings = zoneSettingsMap.value[zoneKey] || { conversionRate: 0.1, productValue: 0 }

                    // This view is the polygon-based opportunity view (engagement → sales).
                    // Use polygon zone_in count as todaysCount so all metrics in the row
                    // share a consistent data source. Tripwire-only zones are filtered out
                    // downstream (sortedOpportunityZones requires hasPolygon).
                    const todaysCount = counter?.zone_in_count ?? 0

                    zones[zoneName] = {
                        flowId,
                        todaysCount,
                        averageDuration: counter?.average_duration ?? 0,
                        zoneType: counter?.zone_type || storeCounter?.zone_type || 'Unknown Zone',
                        count_strategy: counter?.strategy || '',
                        amountWalkBys: counter?.amount_walk_bys || 0,
                        shortLingerers: counter?.amount_short_lingerers || 0,
                        clearLingerers: counter?.amount_clear_lingerers || 0,
                        avgNonWalkbyDuration: counter?.avg_non_walkby_duration || 0,
                        conversionRate: settings.conversionRate,
                        productValue: settings.productValue,
                        hasPolygon: counter != null,
                    }
                }
            }

            totalTodaysCount.value = Object.values(zones)
                .filter(zone => zone.zoneType === "Entrance Zone" || zone.zoneType === "Combined Entrance Exit Zone")
                .reduce((sum, zone) => sum + zone.todaysCount, 0)

            return zones
        }

        const sortedOpportunityZones = computed(() => {
        return Object.entries(processedZones.value)
            .filter(([, zone]) =>
            // Polygon-based opportunity scoring requires polygon dwell data.
            // Tripwire-only Product/Demo zones are surfaced separately in the
            // Tripwire Activity table.
            zone.hasPolygon &&
            (zone.zoneType === 'Product Zone' || zone.zoneType === 'Demo Zone')
            )
            .map(([zoneName, zone]) => {
            const isActive = getZoneStyle(zone.flowId, zone.zoneType).isStreamActive
            const conversionRate = zone.conversionRate ?? 10
            const productValue = zone.productValue ?? 100
            const shortLingerers = zone.shortLingerers ?? 0
            const clearLingerers = zone.clearLingerers ?? 0

            const potential =
                (shortLingerers + clearLingerers) * (conversionRate / 100) * productValue

            return {
                zoneName,
                ...zone,
                isActive,
                salesOpportunity: potential
            }
            })
            .sort((a, b) => {
                // Inactive always go to the bottom
                if (a.isActive !== b.isActive) return a.isActive ? -1 : 1

                // First sort by descending sales opportunity
                if (b.salesOpportunity !== a.salesOpportunity) {
                    return b.salesOpportunity - a.salesOpportunity
                }

                // If salesOpportunity is equal, sort by descending visitor count
                return b.todaysCount - a.todaysCount
            })
        })

        const totalVisitorCount = computed(() => {
            return Object.values(processedZones.value).reduce((sum, zone) => {
                if (zone.zoneType === 'Entrance Zone' || zone.zoneType === 'Combined Entrance Exit Zone') {
                    return sum + zone.todaysCount
                }
                return sum
            }, 0)
        })

        const getColorForZone = (zoneType) => {
            return detection_zone_colors_by_name_solid[zoneType] || 'black'
        }

        
        const isEntranceZonePresent = () => {
            return Object.values(processedZones.value).some(zone => zone.zoneType === "Entrance Zone")
        }

        const isCombinedEntranceExitZonePresent = () => {
            return Object.values(processedZones.value).some(zone => zone.zoneType === "Combined Entrance Exit Zone")
        }

        const isExitZonePresent = () => {
            return false
        }

        onBeforeUnmount(() => {
            // Clear the interval when the component unmounts
            isMounted.value = false
            abortGetCurrentCounters()
            stopInterval()
            if (currentFetchController) {
                currentFetchController.abort()
            }
        })

        // Display only. English keeps exactly the format it always had
        // ("12.5%", "1,234 €"); Dutch follows the glossary ("12,5%", "€ 1.234").
        const pct = (value, decimals) =>
            locale.value === 'nl' ? formatPercent(value, { decimals }) : value.toFixed(decimals) + '%'
        const euro = (value, decimals) => {
            if (locale.value === 'nl') return formatEuro(value, { decimals: decimals ?? (Number.isInteger(Number(value)) ? 0 : 2) })
            return decimals == null ? `${value.toLocaleString()} €` : `${value.toFixed(decimals)} €`
        }
        const zoneTypeTitle = (zoneType) =>
            zoneType === 'Unknown Zone' ? t('realtime.unknownZone') : zoneTypeLabel(zoneType)

        return {
            pct,
            euro,
            zoneTypeTitle,
            isLoadingTodayCount,
            isLoadingTodayCountTable,
            formattedDurationTime,
            getColorForZone,
            formatDifference,
            getDifferenceColor,
            isCardEmpty,
            totalTodaysCount,
            totalTodaysExit,
            pastAverages,
            currentWeekdayString,
            processedZones,
            sortedOpportunityZones,
            flowZoneCounters,
            totalVisitorCount,
            conversionRate,
            productValue,
            getZoneStyle,
            getZoneStyleCurrentIn,
            isEntranceZonePresent,
            isCombinedEntranceExitZonePresent,
            isExitZonePresent,
        }
    }
}

</script>

<style scoped>
.table-wrapper{ border:1px solid #e6edf3; border-radius:10px; overflow:hidden; }
.stora-table{ width:100%; border-collapse:collapse; }
.stora-table th, .stora-table td{ padding:10px; border-bottom:1px solid #eef3f7; }
.stora-table thead th{
  background:rgba(33,37,41,.03); color:#4F7EB3; text-align:center; font-weight:700; font-size:.9rem;
}
.stora-table tbody tr:last-child td{ border-bottom:0; }
.muted{ color:#9aa7b1; }

/* Cell that doesn't apply to this zone's geometry (e.g. dwell columns on a tripwire-only zone) */
.na-cell{ color:#aab4be; font-weight:600; }

/* Small inline glyph next to a zone name to indicate a tripwire is involved */
.geom-marker{ margin-left:6px; color:#4F7EB3; font-size:.78rem; opacity:.85; }

/* visitor bar - unchanged */
.visitor-bar-outer{ position:relative; height:20px; border:1px solid #ddd; border-radius:4px; background:#fff; overflow:hidden; }
.visitor-bar-gradient{
  height:100%;
  background: linear-gradient(to right, #C21500, #FFA500, #2F9E44);
  -webkit-mask-image: linear-gradient(to right, black var(--bar-fill), transparent var(--bar-fill));
  mask-image: linear-gradient(to right, black var(--bar-fill), transparent var(--bar-fill));
  -webkit-mask-size:100% 100%; mask-size:100% 100%;
  -webkit-mask-repeat:no-repeat; mask-repeat:no-repeat;
}
.visitor-bar-gradient.inactive{ background:#e6e6e6 !important; -webkit-mask-image:none !important; mask-image:none !important; }
.visitor-bar-gradient.zero-fill{ visibility:hidden; }
.visitor-bar-label{
  position:absolute; inset:0; display:flex; align-items:center; justify-content:center;
  font-size:.8rem; font-weight:600; color:#2b3a44;
}

.panel-min-140 :deep(.panel-body) { min-height: 140px; }

/* ---------- Responsive “stack to cards” ----------
   1130 (not 1054): in table mode the columns get too wide for the panel and
   clip on the right from ~1110px, so stack to cards just before that. */
@media (max-width: 1130px) {
  /* Visually hide the header row but keep it accessible */
  .stora-table thead {
    position:absolute;
    width:1px; height:1px; overflow:hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
  }

  .stora-table, .stora-table tbody, .stora-table tr, .stora-table td { display:block; width:100%; }
  .stora-table tr {
    border:1px solid #e6edf3;
    border-radius:12px;
    padding:10px;
    margin:10px 10px 14px;
    background:#fff;
    box-shadow:0 3px 8px rgba(13,38,76,.05);
  }

  .stora-table td {
    border:0;
    border-bottom:1px dashed #eef3f7;
    padding:8px 6px;
    display:grid;
    grid-template-columns: 140px 1fr;  /* label | value */
    align-items:center;
    gap:8px;
  }
  .stora-table td:last-child { border-bottom:0; }

  /* inject the header name for each cell from data-th="" */
  .stora-table td::before {
    content: attr(data-th);
    font-weight:600;
    color:#425461;
    font-size:.9rem;
  }

  /* Make percentage bar comfortable in stacked mode */
  .visitor-bar-outer { height:18px; }
}

/* Even narrower: put label above value */
@media (max-width: 520px) {
  .stora-table td { grid-template-columns: 1fr; }
  .stora-table td::before {
    margin-bottom: 2px;
  }
}
</style>